import { Alert, Box, Chip, Typography } from '@mui/material';
import FileTreeSelector, { FileTreeEntry } from 'components/atoms/FileTreeSelector';
import { UploadDashboardRow } from 'interfaces/fileList';
import { filter, forEach, map, orderBy, size, values } from 'lodash';
import React from 'react';
import { formatBytes } from 'utils/helpers';

interface UploadedFilesTreeSelectorProps {
  rows: UploadDashboardRow[];
  selectedUploadKeys: string[];
  canReingestUploadedSlides: boolean;
  onSelectedUploadKeysChange: (selectedUploadKeys: string[]) => void;
}

interface UploadTreeEntry extends FileTreeEntry {
  row?: UploadDashboardRow;
}

const getParentPath = (path: string) => {
  const pathSegments = filter(path.replace(/\/$/, '').split('/'));

  return pathSegments.length > 1 ? `${pathSegments.slice(0, -1).join('/')}/` : '';
};

const getUploadTree = (rows: UploadDashboardRow[]) => {
  const entriesByPath: Record<string, UploadTreeEntry> = {};
  const childrenByPath: Record<string, UploadTreeEntry[]> = {};

  forEach(
    filter(rows, (row) => Boolean(row.uploadKey)),
    (row) => {
      const uploadKey = row.uploadKey!;
      const pathSegments = filter(uploadKey.split('/'));

      forEach(pathSegments.slice(0, -1), (pathSegment, index) => {
        const folderPath = `${pathSegments.slice(0, index + 1).join('/')}/`;

        entriesByPath[folderPath] = entriesByPath[folderPath] || {
          id: folderPath,
          name: pathSegment,
          entryType: 'folder',
        };
      });

      entriesByPath[uploadKey] = {
        id: uploadKey,
        name: row.fileName || row.name,
        entryType: 'file',
        row,
      };
    }
  );

  forEach(values(entriesByPath), (entry) => {
    const parentPath = getParentPath(entry.id);

    childrenByPath[parentPath] = childrenByPath[parentPath] || [];
    childrenByPath[parentPath].push(entry);
  });

  forEach(childrenByPath, (children, parentPath) => {
    childrenByPath[parentPath] = orderBy(
      children,
      [(entry) => (entry.entryType === 'folder' ? 0 : 1), 'name'],
      ['asc']
    );
  });

  return {
    childrenByPath,
    rootEntries: childrenByPath[''] || [],
  };
};

const isSelectableUpload = (row: UploadDashboardRow, canReingestUploadedSlides: boolean) =>
  row.uploadCategory === 'awaitingIngest' || canReingestUploadedSlides;

const UploadedFilesTreeSelector = ({
  rows,
  selectedUploadKeys,
  canReingestUploadedSlides,
  onSelectedUploadKeysChange,
}: UploadedFilesTreeSelectorProps) => {
  const [quickFilter, setQuickFilter] = React.useState('');
  const tree = React.useMemo(() => getUploadTree(rows), [rows]);

  const getFolderDescendantRows = React.useCallback(
    (folderPath: string): UploadDashboardRow[] =>
      (tree.childrenByPath[folderPath] || []).flatMap((entry) =>
        entry.entryType === 'folder' ? getFolderDescendantRows(entry.id) : entry.row ? [entry.row] : []
      ),
    [tree.childrenByPath]
  );

  const getEntrySelectableUploadKeys = React.useCallback(
    (entry: UploadTreeEntry) => {
      const rowsForEntry =
        entry.entryType === 'folder' ? getFolderDescendantRows(entry.id) : entry.row ? [entry.row] : [];

      return map(
        filter(rowsForEntry, (row) => Boolean(row.uploadKey) && isSelectableUpload(row, canReingestUploadedSlides)),
        (row) => row.uploadKey!
      );
    },
    [canReingestUploadedSlides, getFolderDescendantRows]
  );

  if (tree.rootEntries.length === 0) {
    return <Alert severity="info">No uploaded slide files are available for manual ingestion.</Alert>;
  }

  return (
    <FileTreeSelector
      title="Uploaded files"
      subtitle="Select files directly or select a folder to include all selectable files under it."
      rootEntries={tree.rootEntries}
      selectedItemIds={selectedUploadKeys}
      quickFilter={quickFilter}
      onQuickFilterChange={setQuickFilter}
      onSelectedItemIdsChange={onSelectedUploadKeysChange}
      getChildEntries={(entry) => tree.childrenByPath[entry.id] || []}
      getSelectableItemIds={getEntrySelectableUploadKeys}
      getEntryItemCount={(entry) =>
        entry.entryType === 'folder' ? size(getFolderDescendantRows(entry.id)) : undefined
      }
      renderEntryPrimary={(entry, isFolder) => {
        const uploadRow = entry.row;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: isFolder ? 600 : 500 }} noWrap>
              {entry.name}
            </Typography>
            {uploadRow ? (
              <>
                <Chip
                  size="small"
                  label={
                    uploadRow.latestSuccessfulIngestJobArchived
                      ? 'Rejected'
                      : uploadRow.uploadCategory === 'awaitingIngest'
                      ? 'Pending'
                      : 'Completed'
                  }
                  color={uploadRow.uploadCategory === 'awaitingIngest' ? 'default' : 'warning'}
                />
                {uploadRow.willCreateDuplicateOnReingest ? (
                  <Chip size="small" color="warning" variant="outlined" label="Duplicate slide entry" />
                ) : null}
              </>
            ) : null}
          </Box>
        );
      }}
      renderEntrySecondary={(entry) => {
        const uploadRow = entry.row;

        return uploadRow
          ? `${uploadRow.status || 'Unknown'} · ${formatBytes(uploadRow.size)} · ${uploadRow.uploadKey}`
          : undefined;
      }}
      renderEntryStatus={(entry, depth) =>
        entry.row && getEntrySelectableUploadKeys(entry).length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 0.75, pl: 7 + depth * 2 }}>
            Re-ingest permission is required to include completed uploads in a bulk run.
          </Typography>
        ) : null
      }
      listAriaLabel="uploaded file tree"
      listSx={{ maxHeight: 360 }}
    />
  );
};

export default UploadedFilesTreeSelector;
